import type { Partner } from "@expert/shared-types";
import { Flex, Group, Text, Textarea } from "@mantine/core";
import { type JSX, useCallback } from "react";
import { Quotes, RadioCard } from "@/common-ui";
import { useFeatureFlag } from "@/shared-utils";
import type { SummaryResolution } from "../store/useSessionSummaryStore";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import classes from "./EditSessionSummaryModal.module.css";
import { SendSummaryNotification } from "./SendSummaryNotification";
import { MAX_SUMMARY_LENGTH } from "./utils";

interface SessionSummaryFormProps {
    partner: Partner;
    loading: boolean;
    shouldShowTextArea: boolean;
    shouldDisableTextArea: boolean;
    canSendSuccessfulSummary: boolean;
}

export function SessionSummaryForm({
    partner,
    loading,
    shouldDisableTextArea,
    shouldShowTextArea,
    canSendSuccessfulSummary,
}: SessionSummaryFormProps): JSX.Element | null {
    const { setSessionSummary, setResolved, hideResolutionStatusForm, summary, resolutionStatus } =
        useSessionSummaryStore();

    const { data: shouldHideUnresolvedDisposition } = useFeatureFlag("EWP-shouldHideUnresolvedDisposition", {
        partner,
    });

    const shouldHideResolutionForm = hideResolutionStatusForm && resolutionStatus !== null;

    const handleResolution = useCallback(
        (resolution: SummaryResolution) => {
            setResolved(resolution);
        },
        [setResolved],
    );

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setSessionSummary({ value: event.target.value });
        },
        [setSessionSummary],
    );

    return (
        <>
            {!shouldShowTextArea && (
                <SendSummaryNotification
                    partner={partner}
                    loading={loading}
                    summarySendSucceeded={canSendSuccessfulSummary}
                />
            )}
            {shouldShowTextArea && (
                <Flex className={classes.modalTextareaContainer} direction="column">
                    <Textarea
                        className="fs-mask"
                        classNames={{
                            input: classes.modalTextarea,
                            section: classes.modalTextareaSection,
                        }}
                        maxLength={MAX_SUMMARY_LENGTH}
                        leftSection={shouldDisableTextArea ? <Quotes width={19} height={14} /> : null}
                        mr="0.125rem"
                        mt="3xs"
                        mb="0.3rem"
                        onChange={handleChange}
                        disabled={shouldDisableTextArea}
                        placeholder="Add the issue and resolution steps."
                        rows={5}
                        value={summary}
                        data-testid="session-summary-text-input"
                    />
                </Flex>
            )}
            {!shouldHideResolutionForm && (
                <>
                    <Text size="sm" mt="xl" mb=".5rem">
                        {shouldHideUnresolvedDisposition ? "Call outcome:" : "Was the customer’s issue resolved?"}
                    </Text>
                    <Group>
                        <RadioCard
                            id="resolved-radio-button"
                            analytics={{
                                actionId: "SessionSummaryModal_ProblemSolvedYes",
                            }}
                            color="var(--mantine-color-dark-5)"
                            label={shouldHideUnresolvedDisposition ? "End" : "Yes"}
                            onClick={() => handleResolution("resolved")}
                            p="0.5rem 0.75rem"
                            selected={resolutionStatus === "resolved"}
                            data-testid="session-summary-problem-solved-yes"
                            autoFocus={false}
                            tabIndex={-1}
                        />
                        {!shouldHideUnresolvedDisposition && (
                            <RadioCard
                                id="not-resolved-radio-button"
                                analytics={{
                                    actionId: "SessionSummaryModal_ProblemSolvedNo",
                                }}
                                color="var(--mantine-color-dark-5)"
                                label="No"
                                p="0.5rem 0.75rem"
                                onClick={() => handleResolution("not resolved")}
                                selected={resolutionStatus === "not resolved"}
                                data-testid="session-summary-problem-solved-no"
                                autoFocus={false}
                                tabIndex={-1}
                            />
                        )}
                        <RadioCard
                            id="transfer-radio-button"
                            analytics={{
                                actionId: "SessionSummaryModal_ProblemSolvedTransferred",
                            }}
                            p="0.5rem 0.75rem"
                            color="var(--mantine-color-dark-5)"
                            label="Transferred"
                            onClick={() => handleResolution("transferred")}
                            selected={resolutionStatus === "transferred"}
                            data-testid="session-summary-problem-solved-transferred"
                            autoFocus={false}
                            tabIndex={-1}
                        />
                    </Group>
                </>
            )}
        </>
    );
}
